import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickAwayListener, MenuList, Paper, styled } from '@material-ui/core';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { useUser } from '../../../shared';
import UserMenuListItem from './UserMenuListItem';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { setModalProps } from 'src/modules/shared/slices/privatePageSettings/entity/privatePageSettingsSlice';
import Cookies from 'js-cookie';
// import { resetBanner, setBannerState } from 'src/modules/shared/slices/banner/entity/bannerSlice';
import { logOff } from 'src/modules/shared/slices/logout/slice';
import { persistor } from 'src/modules/shared/store';
import { usePostLeaveImpersonateMutation } from 'src/modules/shared/slices/impersonate/impersonateApi';
import SpeechBubbleTooltip from 'src/components/SpeechBubbleTooltip/SpeechBubbleTooltip';
import { currentEnv } from 'src';
import { getContent, UserMenuTypes } from './UserMenuTooltips';

type Props = {
  onClose: (event?: React.MouseEvent<EventTarget>) => void;
};

const StyledPaper = styled(Paper)({
  position: 'absolute',
  top: '5rem',
  left: 0,
  width: '100%',
});

const env = process.env.REACT_APP_ENVIRONMENT;

const UserMenuPopup: FC<Props> = ({ onClose }) => {
  // hooks
  const { t } = useTranslation();
  const log = useDataLogging();
  const { getPath } = useI18nPath();
  const { isTeacher, isSuperAdmin, isImpersonatedUser } = useUser();
  const dispatch = useAppDispatch();

  // redux
  const modalProps = useAppSelector((state) => state.privatePageSettings.modalProps);
  // const showBanner = useAppSelector((state) => state.banner.showBanner);
  const [leaveImpersonation, leaveImpersonationResponse] = usePostLeaveImpersonateMutation();

  const handleImpersonation = () => {
    dispatch(setModalProps({ ...modalProps, opened: true }));
    onClose();
  };

  const handleLeaveImpersonation = () => {
    leaveImpersonation();
  };

  const handleSelfServiceClick = () => {
    log(LogActionTypes.SELF_SERVICE_OPEN);
    onClose();
  };

  useEffect(() => {
    if (leaveImpersonationResponse.isSuccess) {
      leaveImpersonationResponse.reset();
      onClose();
    }
  }, [leaveImpersonationResponse, onClose]);

  const handleLogout = useCallback(async () => {
    if (Cookies.get('hubspotutk') && env !== 'demo') {
      Cookies.remove('hubspotutk');
      Cookies.remove('hubspotutk', { path: '/', domain: '.lernova.be' });
    }
    log(LogActionTypes.USER_LOGOUT);
    dispatch(logOff());
    await persistor.purge();
  }, [dispatch, log]);

  // const handleBanner = () => {
  //   if (showBanner) {
  //     dispatch(resetBanner());
  //     return onClose();
  //   }
  //   dispatch(
  //     setBannerState({
  //       showBanner: true,
  //       targetRoles: ['teacher'],
  //       urlDescription: 'app.lernova.be',
  //       messageStart: 'Je bezoekt onze demo-omgeving. Bezoek ',
  //       url: `https://app.lernova.be`,
  //       messageEnd: ' om te werken met je leerlingen.',
  //       justifyContent: 'flex-start',
  //       type: 'demo',
  //     })
  //   );
  //   return onClose();
  // };
  const [open, setOpen] = useState<UserMenuTypes>(null);
  const getTooltipProps = useCallback(
    (type: UserMenuTypes) => {
      return {
        disableHoverListener: true,
        title: getContent(type),
        onMouseLeave: () => setOpen(null),
        onMouseEnter: () => setOpen(type),
        onClick: () => setOpen(type),
        open: open === type,
      };
    },
    [open]
  );

  return (
    <StyledPaper id="menu-list-grow">
      <ClickAwayListener onClickAway={onClose}>
        <MenuList>
          <UserMenuListItem
            label={t('shared.userMenu.profile')}
            to={getPath('profile')}
            icon={SpriteIconsLight.CIRCLE_USER}
            onClick={onClose}
          />
          {isSuperAdmin && !isImpersonatedUser && (
            <UserMenuListItem
              label="Impersonate"
              icon={SpriteIconsLight.GHOST}
              onClick={handleImpersonation}
            />
          )}
          {isImpersonatedUser && (
            <UserMenuListItem
              label="Leave Impersonation"
              icon={SpriteIconsLight.ARROW_RIGHT_FROM_BRACKET}
              onClick={handleLeaveImpersonation}
            />
          )}

          {isTeacher && (
            <SpeechBubbleTooltip {...getTooltipProps('assignmentDashboard')}>
              <UserMenuListItem
                label={t('assignmentDashboard.title')}
                icon={SpriteIconsLight.CLIPBOARD_CHECK}
                to={currentEnv === 'demo' ? '' : getPath('assignmentDashboard')}
                onClick={onClose}
                isDisabled={currentEnv === 'demo'}
              />
            </SpeechBubbleTooltip>
          )}
          {isTeacher && (
            <SpeechBubbleTooltip {...getTooltipProps('groups')}>
              <UserMenuListItem
                label={t('groups.pageTitle')}
                icon={SpriteIconsLight.USERS_VIEWFINDER}
                to={currentEnv === 'demo' ? '' : getPath('groups')}
                onClick={onClose}
                isDisabled={currentEnv === 'demo'}
              />
            </SpeechBubbleTooltip>
          )}
          {isTeacher && (
            <SpeechBubbleTooltip {...getTooltipProps('classAndMethods')}>
              <UserMenuListItem
                label={'Klassen & methodes'}
                icon={SpriteIconsLight.PEOPLE_ROOF}
                to={currentEnv === 'demo' ? '' : getPath('gradesManager')}
                onClick={handleSelfServiceClick}
                isDisabled={currentEnv === 'demo'}
              />
            </SpeechBubbleTooltip>
          )}
          {/* {isTeacher && (env === 'staging' || env === 'local') && (
            <UserMenuListItem
              label={`${showBanner ? 'Hide' : 'Show'} banner`}
              icon={SpriteIconsLight.BULLSEYE_ARROW}
              onClick={handleBanner}
            />
          )} */}
          <UserMenuListItem
            label={t('shared.userMenu.logout')}
            icon={SpriteIconsLight.ARROW_RIGHT_FROM_BRACKET}
            onClick={() => handleLogout()}
          />
        </MenuList>
      </ClickAwayListener>
    </StyledPaper>
  );
};

export default UserMenuPopup;
